import React, { useRef } from 'react';
import classNames from 'classnames';
import { hoc } from 'src/core';
import { InputProps, useInput } from './input.props';
import styles from './input.module.scss';
import Autocomplete from 'react-google-autocomplete';

import _Mask, * as InputMask from 'react-input-mask';
import { useCancelDoubleSpaceDotMacBehaviour } from 'src/core/hooks';

/**
 * !Typings retard alert!
 */
const Mask: typeof _Mask = InputMask as any;

/**
 * Renders Input
 */
const Input = hoc(
  useInput,
  ({
    id,
    refInput,
    size,
    mask,
    type,
    input,
    value,
    name,
    label,
    error,
    success,
    isError,
    focused,
    _onBlur,
    disabled,
    _onFocus,
    maskChar,
    hasValue,
    className,
    maxLength,
    onKeyDown,
    onKeyUp,
    onKeyPress,
    suggestions,
    onInputChange,
    onSuggestionClick,
    isSuggestionsVisible,
    onPlaceSelected,
    placeholder,
    required,
    values,
    autocomplete,
    alwaysShowMask = false,
    phone,
    googleAutoComplete,
    geometryBounds
  }: InputProps) => {
    const autocompleteRef = useRef<HTMLInputElement>();
    const {
      _onKeyDown,
      _onKeyUp,
      _onKeyPress
    } = useCancelDoubleSpaceDotMacBehaviour({
      onKeyUp,
      onKeyDown,
      onKeyPress,
      onChange: onInputChange
    });

    return (
      <div
        className={classNames(styles.input, styles[size], className, {
          [styles.focused]: focused,
          [styles['has-value']]: hasValue || (mask && value),
          [styles['input-error']]: !!error || isError,
          [styles.focused_disable]: disabled
        })}
      >
        {label && (
          <label
            id='error_label'
            className={classNames(styles.label, {
              [styles['label-error']]: isError || !!error,
              [styles.label_disabled]: disabled
            })}
            onClick={event => {
              if (googleAutoComplete) {
                autocompleteRef.current?.focus();

                return;
              }

              input.current
                ? input?.current?.focus()
                : //@ts-ignore
                  event.target?.nextSibling?.focus();
            }}
          >
            {label}
            {required && '*'}
          </label>
        )}

        <div
          className={classNames(styles.inputWrapper, {
            [styles.disabled]: disabled
          })}
        >
          {googleAutoComplete ? (
            <Autocomplete
              ref={autocompleteRef}
              className={classNames(styles.mask, className, {
                [styles['mask-error']]: !!error || isError
              })}
              options={{
                bounds: geometryBounds,
                componentRestrictions: { country: ['UA'] },
                fields: ['address_components', 'geometry', 'name'],
                strictBounds: true,
                types: ['address']
              }}
              onBlur={_onBlur}
              onKeyDown={_onKeyDown}
              onKeyUp={_onKeyUp}
              onKeyPress={_onKeyPress}
              onFocus={_onFocus}
              onPlaceSelected={onPlaceSelected}
              // placeholder={placeholder || focused || hasValue || alwaysShowMask}
              placeholder={placeholder}
              value={value}
              onChange={onInputChange}
              name={name}
            />
          ) : (
            <Mask
              id={id}
              className={classNames(styles.mask, className, {
                [styles['mask-error']]: !!error || isError,
                [styles['mask-disabled']]: disabled
              })}
              name={name}
              type={type}
              mask={mask}
              // @ts-ignore
              maskChar={maskChar}
              onBlur={_onBlur}
              onKeyDown={_onKeyDown}
              onKeyUp={_onKeyUp}
              onKeyPress={_onKeyPress}
              // @ts-ignore
              inputRef={ref =>
                refInput ? (refInput.current = ref) : (input.current = ref)
              }
              onFocus={_onFocus}
              disabled={disabled}
              value={value}
              onChange={onInputChange}
              maxLength={maxLength}
              // placeholder={placeholder || focused || hasValue || alwaysShowMask}
              placeholder={placeholder}
              alwaysShowMask={alwaysShowMask}
              autoComplete={autocomplete}
              // {...(props as any)}
            />
          )}
        </div>

        {mask && (value == 'undefined' || !value) && !focused && phone && (
          <div
            className={classNames(styles.phone, {
              [styles['phone-error']]: isError || !!error
            })}
          >
            +38
          </div>
        )}

        {(isError || !!error) && (
          <label className={styles.error}>{error}</label>
        )}
        {(isError || !!error) && <div className={styles['error-sign']}>!</div>}
        {success && (
          <div className={styles.success_container}>
            <img
              src='/img/checkmark-icon.svg'
              width='20'
              height='20'
              alt='checkmark'
            />
          </div>
        )}

        {isSuggestionsVisible && (
          <div className={styles.suggestions}>
            {(suggestions as any[]).map((suggestion, index) => (
              <div
                key={index}
                className={styles.suggestion}
                onMouseDown={event => onSuggestionClick(event, suggestion)}
              >
                {typeof suggestion == 'string' ? suggestion : suggestion.name}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
);

/**
 * Input Default Props
 */
Input.defaultProps = {
  // @ts-ignore
  size: 'medium',
  mask: null,
  type: 'text',
  maskChar: '',
  api: () => {},
  onFocus: () => {}
};

export { Input };
